import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Sparkles, Home, Wifi, Sofa, MapPin, Clock, Shield, Coffee, Menu, X, Pencil } from 'lucide-react';
import { LandingEditor } from '@/components/landing/LandingEditor';

// Backend base URL — used to fetch the CMS config & featured homes.
const API = process.env.REACT_APP_BACKEND_URL;

// Icons available to the CMS. Any string in cfg.hero.trust_badges[i].icon
// picks the matching lucide icon; unknown values fall back to Sparkles.
const ICONS = { shield: Shield, wifi: Wifi, coffee: Coffee, clock: Clock, sofa: Sofa, 'map-pin': MapPin, sparkles: Sparkles, home: Home };

// ─── Default content — used until the CMS config loads / when a field is
// left blank in the editor. Also serves as the schema hint. ────────────────
const DEFAULT_HERO_IMAGES = [
  'https://images.unsplash.com/photo-1615529182904-14819c35db37?w=1800&q=80&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=1800&q=80&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=1800&q=80&auto=format&fit=crop',
];

const VALUE_PROPS = [
  { icon: Clock, title: '30+ Day Stays', body: 'Flexible lease terms designed for one month, one season, or as long as you need.' },
  { icon: Sofa, title: 'Fully Furnished', body: 'Every piece — down to the linens and lamps — thoughtfully chosen for calm daily living.' },
  { icon: Wifi, title: 'All Utilities Included', body: 'Fast Wi-Fi, streaming, and monthly essentials — one predictable bill covers it all.' },
  { icon: MapPin, title: 'Prime Neighborhoods', body: 'Walkable, well-lit locations near hospitals, transit hubs, and downtown cores.' },
];

const AUDIENCES = [
  { emoji: '🏥', title: 'Traveling Healthcare', body: 'Rest well between shifts. Fast move-in for 13-week contracts and beyond.' },
  { emoji: '💼', title: 'Corporate Travelers', body: 'Professional stays with invoicing, corporate housing coordination, and long-stay pricing.' },
  { emoji: '📦', title: 'Relocating', body: 'A soft landing while your permanent home comes together.' },
  { emoji: '🎓', title: 'Graduate Students', body: 'Fully furnished, semester-friendly stays close to campus.' },
  { emoji: '🌍', title: 'Digital Nomads', body: 'Quiet workspaces, ergonomic setups, and reliable connectivity in every home.' },
  { emoji: '🤝', title: 'Consultants', body: 'Skip the hotel routine. Cook your own meals, keep your rhythm, unwind on your own terms.' },
];

const TESTIMONIALS = [
  { quote: 'Everything about the place felt considered — the light, the linens, the little jar of coffee in the kitchen. I forgot I was traveling.', name: 'Maya R.', role: 'Traveling Nurse · 3-month stay' },
  { quote: 'The check-in was seamless and the workspace was better than my office. I extended twice.', name: 'David T.', role: 'Management Consultant · Extended stay' },
  { quote: 'Felt like a small boutique hotel that just happened to have a real kitchen and a great bed.', name: 'Priya S.', role: 'Relocating Executive' },
];

const FEATURED_HOMES = [
  { title: 'The Marina Studio', neighborhood: 'Marina · San Francisco', image: 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=1400&q=80&auto=format&fit=crop', tag: 'Sun-Filled Studio' },
  { title: 'Presidio Two-Bedroom', neighborhood: 'Presidio Heights', image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1400&q=80&auto=format&fit=crop', tag: 'Family-Friendly' },
  { title: 'Hayes Valley One-Bed', neighborhood: 'Hayes Valley', image: 'https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?w=1400&q=80&auto=format&fit=crop', tag: 'Boutique Feel' },
];

// ─── Reusable observer for scroll-triggered fade-ins ─────────────
function useReveal(delay = 0) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting) { setVisible(true); io.unobserve(el); }
      }
    }, { threshold: 0.15, rootMargin: '0px 0px -40px 0px' });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return {
    ref,
    className: `transition-all duration-[900ms] ease-out ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`,
    style: { transitionDelay: `${delay}ms` },
  };
}

// Small wrapper so we can call useReveal inside a .map() without hitting
// rules-of-hooks issues when the mapped array's length changes at runtime.
function Reveal({ delay = 0, className = '', as: Tag = 'div', ...rest }) {
  const r = useReveal(delay);
  return <Tag {...r} {...rest} className={`${r.className} ${className}`} />;
}

// ─── Sticky top nav (shrinks on scroll) ─────────────────────────
function StickyNav({ onBookNow, brandName, bookNowLabel }) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const links = [
    { href: '#homes', label: 'Homes' },
    { href: '#whoitsfor', label: 'Who It’s For' },
    { href: '#howitworks', label: 'How It Works' },
    { href: '#contact', label: 'Contact' },
  ];

  return (
    <nav
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-[#F7F6F2]/95 backdrop-blur-md border-b border-[#D7E0D5] py-2.5'
          : 'bg-transparent py-4'
      }`}
      data-testid="flexliving-nav"
    >
      <div className="max-w-[1240px] mx-auto px-5 sm:px-8 flex items-center justify-between">
        <a href="/" className="flex items-center gap-2 group" data-testid="flexliving-logo">
          <div className="h-9 w-9 rounded-full bg-[#4E6A52] flex items-center justify-center text-white transition-transform group-hover:scale-105">
            <Home className="h-4 w-4" strokeWidth={2.2} />
          </div>
          <span className="text-[15px] font-medium tracking-tight text-[#2F3431]">{brandName || 'Flex Living'}</span>
        </a>

        <div className="hidden md:flex items-center gap-7 text-sm text-[#2F3431]/80">
          {links.map(l => (
            <a key={l.href} href={l.href} className="hover:text-[#4E6A52] transition-colors relative after:absolute after:inset-x-0 after:-bottom-1 after:h-px after:bg-[#4E6A52] after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:origin-left">
              {l.label}
            </a>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={onBookNow}
            className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#4E6A52] text-white text-sm font-medium hover:bg-[#3d5541] transition-all hover:shadow-[0_8px_24px_-8px_rgba(78,106,82,0.5)] hover:-translate-y-0.5"
            data-testid="nav-book-now"
          >
            {bookNowLabel || 'Book Now'} <ArrowRight className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => setMenuOpen(v => !v)}
            className="md:hidden h-9 w-9 inline-flex items-center justify-center rounded-full border border-[#D7E0D5] text-[#2F3431]"
            aria-label="Menu"
            data-testid="nav-menu-toggle"
          >
            {menuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-[#D7E0D5] bg-[#F7F6F2] px-5 py-4 space-y-3 shadow-md">
          {links.map(l => (
            <a key={l.href} href={l.href} onClick={() => setMenuOpen(false)} className="block text-sm text-[#2F3431]">
              {l.label}
            </a>
          ))}
          <button onClick={() => { setMenuOpen(false); onBookNow(); }}
            className="w-full inline-flex justify-center items-center gap-1.5 px-4 py-2.5 rounded-full bg-[#4E6A52] text-white text-sm font-medium">
            Book Now <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </nav>
  );
}

// ─── Hero ─────────────────────────────────────────────────────
function Hero({ onBookNow, cfg }) {
  const HERO = cfg?.hero || {};
  const heroImages = (HERO.images && HERO.images.length) ? HERO.images : DEFAULT_HERO_IMAGES;
  const titleTop = HERO.title_top || 'Stay a month.';
  const titleBottom = HERO.title_bottom || 'Live like home.';
  const subtitle = HERO.subtitle || '';
  const badge = HERO.badge || 'Fully furnished stays · from 30 days';
  const bookNowLabel = HERO.book_now_label || 'Book Now';
  const trustBadges = HERO.trust_badges || [];
  const floatingCard = HERO.floating_card || {};
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx(i => (i + 1) % heroImages.length), 6500);
    return () => clearInterval(t);
  }, [heroImages.length]);
  const badgeReveal = useReveal(80);
  const title = useReveal(160);
  const sub = useReveal(280);
  const cta = useReveal(400);
  return (
    <section className="relative overflow-hidden bg-[#F7F6F2] pt-28 pb-16 sm:pt-36 sm:pb-24">
      {/* Soft gradient blob backdrop */}
      <div className="pointer-events-none absolute -top-24 -left-24 h-[420px] w-[420px] rounded-full bg-[#A8B8A5]/25 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -right-16 h-[520px] w-[520px] rounded-full bg-[#7A9B76]/15 blur-3xl" />

      <div className="max-w-[1240px] mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-16 items-center">
        <div>
          <span {...badgeReveal} className={`inline-flex items-center gap-2 text-[11px] font-medium tracking-[0.16em] text-[#4E6A52] uppercase bg-[#EEF2EC] rounded-full px-3.5 py-1.5 border border-[#D7E0D5] ${badgeReveal.className}`}>
            <Sparkles className="h-3 w-3" /> {badge}
          </span>
          <h1
            {...title}
            className={`mt-6 font-serif text-[42px] sm:text-[56px] lg:text-[68px] leading-[1.02] tracking-tight text-[#2F3431] ${title.className}`}
            style={{ ...title.style, fontFamily: '"Fraunces", "Cormorant Garamond", Georgia, serif' }}
            data-testid="hero-title"
          >
            {titleTop}<br />
            <span className="text-[#4E6A52] italic">{titleBottom}</span>
          </h1>
          <p {...sub} className={`mt-6 text-[17px] sm:text-lg text-[#2F3431]/70 max-w-xl leading-relaxed ${sub.className}`}>
            {subtitle}
          </p>
          <div {...cta} className={`mt-9 flex flex-wrap items-center gap-3 ${cta.className}`}>
            <button
              onClick={onBookNow}
              className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#4E6A52] text-white text-[15px] font-medium hover:bg-[#3d5541] transition-all hover:shadow-[0_12px_32px_-8px_rgba(78,106,82,0.55)] hover:-translate-y-0.5"
              data-testid="hero-book-now"
            >
              {bookNowLabel}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </button>
            <a
              href="#homes"
              className="inline-flex items-center gap-2 px-5 py-3.5 rounded-full text-[15px] font-medium text-[#4E6A52] hover:bg-[#EEF2EC] transition-colors"
            >
              Explore homes <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
          <div className="mt-10 flex flex-wrap items-center gap-6 text-xs text-[#2F3431]/55">
            {trustBadges.map((t, i) => {
              const Icon = ICONS[t.icon] || Sparkles;
              return (
                <div key={i} className="flex items-center gap-1.5"><Icon className="h-3.5 w-3.5 text-[#4E6A52]" /> {t.label}</div>
              );
            })}
          </div>
        </div>

        {/* Rotating hero image */}
        <div className="relative">
          <div className="relative aspect-[4/5] w-full rounded-[28px] overflow-hidden shadow-[0_30px_80px_-30px_rgba(47,52,49,0.35)]">
            {heroImages.map((src, i) => {
              const url = src && !src.startsWith('http') ? `${API}${src}` : src;
              return (
                <img
                  key={src}
                  src={url}
                  alt="Furnished home"
                  loading={i === 0 ? 'eager' : 'lazy'}
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${idx === i ? 'opacity-100' : 'opacity-0'}`}
                />
              );
            })}
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" />
          </div>
          {/* Small floating card */}
          {(floatingCard.eyebrow || floatingCard.label) && (
            <div className="hidden sm:block absolute -bottom-6 -left-6 bg-white/95 backdrop-blur rounded-2xl px-4 py-3 shadow-[0_20px_50px_-16px_rgba(47,52,49,0.25)] border border-[#D7E0D5]">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-[#EEF2EC] flex items-center justify-center">
                  <Home className="h-4 w-4 text-[#4E6A52]" />
                </div>
                <div>
                  <p className="text-[11px] text-[#2F3431]/60">{floatingCard.eyebrow || ''}</p>
                  <p className="text-sm font-medium text-[#2F3431] leading-tight">{floatingCard.label || ''}</p>
                </div>
              </div>
            </div>
          )}
          {/* Rotating pips */}
          <div className="mt-4 flex justify-center gap-1.5">
            {heroImages.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                aria-label={`Show image ${i + 1}`}
                className={`h-1.5 rounded-full transition-all ${idx === i ? 'w-6 bg-[#4E6A52]' : 'w-1.5 bg-[#D7E0D5]'}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Value propositions ────────────────────────────────────────
function ValueProps({ cfg }) {
  const items = cfg?.value_props || [];
  const H = cfg?.value_props_heading || {};
  const heading = useReveal(60);
  return (
    <section className="py-20 sm:py-28 bg-[#F7F6F2]" id="values">
      <div className="max-w-[1240px] mx-auto px-5 sm:px-8">
        <div {...heading} className={`max-w-2xl ${heading.className}`}>
          <p className="text-[11px] uppercase tracking-[0.18em] text-[#4E6A52] font-medium">{H.eyebrow || 'Why guests stay'}</p>
          <h2 className="mt-3 font-serif text-3xl sm:text-[42px] leading-tight text-[#2F3431]"
              style={{ fontFamily: '"Fraunces", "Cormorant Garamond", Georgia, serif' }}>
            {H.title || 'A softer alternative to hotels & a smarter one to leases.'}
          </h2>
        </div>
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {items.map((v, i) => {
            const Icon = ICONS[v.icon] || Sparkles;
            return (
              <Reveal
                key={i}
                delay={i * 100}
                className="group rounded-2xl bg-white border border-[#D7E0D5] p-6 transition-all hover:-translate-y-1 hover:shadow-[0_20px_40px_-16px_rgba(47,52,49,0.15)]"
                data-testid={`value-prop-${i}`}
              >
                <div className="h-11 w-11 rounded-xl bg-[#EEF2EC] flex items-center justify-center text-[#4E6A52] group-hover:bg-[#4E6A52] group-hover:text-white transition-colors">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="mt-5 text-base font-medium text-[#2F3431]">{v.title}</h3>
                <p className="mt-2 text-sm text-[#2F3431]/65 leading-relaxed">{v.body}</p>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ─── Featured homes ────────────────────────────────────────────
function FeaturedHomes({ onBookNow, cfg, homes }) {
  const H = cfg?.homes_heading || {};
  const heading = useReveal(60);
  const items = (homes || []).slice(0, cfg?.homes_settings?.limit || 3);
  const fallbackImg = 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=1400&q=80&auto=format&fit=crop';
  const openHome = (home) => {
    // Prefer the specific listing detail URL. Falls back to the search page
    // if the backend didn't provide one (older shape).
    if (home?.detail_url) window.location.href = home.detail_url;
    else onBookNow();
  };
  return (
    <section className="py-20 sm:py-28 bg-[#EEF2EC]" id="homes">
      <div className="max-w-[1240px] mx-auto px-5 sm:px-8">
        <div {...heading} className={`flex items-end justify-between flex-wrap gap-6 ${heading.className}`}>
          <div className="max-w-2xl">
            <p className="text-[11px] uppercase tracking-[0.18em] text-[#4E6A52] font-medium">{H.eyebrow || 'Featured homes'}</p>
            <h2 className="mt-3 font-serif text-3xl sm:text-[42px] leading-tight text-[#2F3431]"
                style={{ fontFamily: '"Fraunces", "Cormorant Garamond", Georgia, serif' }}>
              {H.title || 'Warm interiors, bright windows, real kitchens.'}
            </h2>
          </div>
          <button
            onClick={onBookNow}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-[#4E6A52] hover:text-[#3d5541] transition-colors"
          >
            See all availability <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          {items.length === 0 && (
            <p className="col-span-3 text-sm text-[#2F3431]/60 italic">No homes available right now — check back soon.</p>
          )}
          {items.map((h, i) => {
            const photoUrl = h.photo_url
              ? (h.photo_url.startsWith('http') ? h.photo_url : `${API}${h.photo_url}`)
              : fallbackImg;
            return (
              <Reveal
                as="button"
                key={h.id || i}
                delay={i * 120}
                onClick={() => openHome(h)}
                className="group text-left rounded-3xl overflow-hidden bg-white border border-[#D7E0D5] transition-all hover:-translate-y-1 hover:shadow-[0_28px_60px_-24px_rgba(47,52,49,0.22)]"
                data-testid={`featured-home-${i}`}
              >
                <div className="relative aspect-[4/3] overflow-hidden bg-[#EEF2EC]">
                  <img src={photoUrl} alt={h.title} loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-105" />
                  {h.tag && (
                    <span className="absolute top-3 left-3 text-[10px] uppercase tracking-wider bg-white/90 backdrop-blur px-2 py-1 rounded-full text-[#4E6A52] font-medium">
                      {h.tag}
                    </span>
                  )}
                </div>
                <div className="p-5 flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-[15px] font-medium text-[#2F3431] truncate">{h.title}</p>
                    {(h.neighborhood || h.property_name) && (
                      <p className="mt-1 text-xs text-[#2F3431]/55 flex items-center gap-1">
                        <MapPin className="h-3 w-3" />{h.neighborhood || h.property_name}
                      </p>
                    )}
                    {(h.bedrooms != null || h.bathrooms != null || h.starting_price != null) && (
                      <div className="mt-2.5 flex items-center gap-3 text-[11px] text-[#2F3431]/70">
                        {h.bedrooms != null && <span>{h.bedrooms} bd</span>}
                        {h.bathrooms != null && <span>{h.bathrooms} ba</span>}
                        {h.starting_price != null && (
                          <span className="ml-auto font-medium text-[#4E6A52]">
                            from ${Number(h.starting_price).toLocaleString()}<span className="text-[#2F3431]/50 font-normal">/mo</span>
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="h-9 w-9 rounded-full bg-[#EEF2EC] flex items-center justify-center text-[#4E6A52] group-hover:bg-[#4E6A52] group-hover:text-white transition-colors flex-shrink-0">
                    <ArrowUpRight className="h-4 w-4" />
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ─── Who It's For ──────────────────────────────────────────────
function WhoItsFor({ cfg }) {
  const items = cfg?.audiences || [];
  const H = cfg?.audiences_heading || {};
  const heading = useReveal(60);
  return (
    <section className="py-20 sm:py-28 bg-[#F7F6F2]" id="whoitsfor">
      <div className="max-w-[1240px] mx-auto px-5 sm:px-8">
        <div {...heading} className={`max-w-2xl ${heading.className}`}>
          <p className="text-[11px] uppercase tracking-[0.18em] text-[#4E6A52] font-medium">{H.eyebrow || 'Who it\u2019s for'}</p>
          <h2 className="mt-3 font-serif text-3xl sm:text-[42px] leading-tight text-[#2F3431]"
              style={{ fontFamily: '"Fraunces", "Cormorant Garamond", Georgia, serif' }}>
            {H.title || 'Built for people whose calendars don\u2019t fit inside a lease.'}
          </h2>
        </div>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {items.map((a, i) => {
            return (
              <Reveal key={i} delay={(i % 3) * 100}
                className="rounded-2xl p-6 bg-white border border-[#D7E0D5] transition-all hover:-translate-y-1 hover:shadow-[0_18px_38px_-18px_rgba(47,52,49,0.15)]">
                <div className="text-2xl">{a.emoji}</div>
                <h3 className="mt-4 text-base font-medium text-[#2F3431]">{a.title}</h3>
                <p className="mt-2 text-sm text-[#2F3431]/65 leading-relaxed">{a.body}</p>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ─── How It Works ──────────────────────────────────────────────
function HowItWorks({ cfg }) {
  const steps = cfg?.how_it_works || [];
  const H = cfg?.how_it_works_heading || {};
  const heading = useReveal(60);
  return (
    <section className="py-20 sm:py-28 bg-[#EEF2EC]" id="howitworks">
      <div className="max-w-[1240px] mx-auto px-5 sm:px-8">
        <div {...heading} className={`max-w-2xl ${heading.className}`}>
          <p className="text-[11px] uppercase tracking-[0.18em] text-[#4E6A52] font-medium">{H.eyebrow || 'How it works'}</p>
          <h2 className="mt-3 font-serif text-3xl sm:text-[42px] leading-tight text-[#2F3431]"
              style={{ fontFamily: '"Fraunces", "Cormorant Garamond", Georgia, serif' }}>
            {H.title || 'Three steps between here and home.'}
          </h2>
        </div>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
          {steps.map((s, i) => {
            return (
              <Reveal key={s.n || i} delay={i * 110}
                className="relative rounded-2xl bg-white p-8 border border-[#D7E0D5]">
                <p className="font-serif text-[32px] text-[#7A9B76]"
                   style={{ fontFamily: '"Fraunces", "Cormorant Garamond", Georgia, serif' }}>
                  {s.n}
                </p>
                <h3 className="mt-2 text-lg font-medium text-[#2F3431]">{s.title}</h3>
                <p className="mt-2 text-sm text-[#2F3431]/65 leading-relaxed">{s.body}</p>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ─── Testimonials ──────────────────────────────────────────────
function Testimonials({ cfg }) {
  const items = cfg?.testimonials || [];
  const H = cfg?.testimonials_heading || {};
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (items.length <= 1) return;
    const t = setInterval(() => setIdx(i => (i + 1) % items.length), 7000);
    return () => clearInterval(t);
  }, [items.length]);
  const r = useReveal(60);
  const t = items[idx];
  if (!t) return null;
  return (
    <section className="py-20 sm:py-28 bg-[#F7F6F2]">
      <div className="max-w-3xl mx-auto px-5 sm:px-8 text-center">
        <div {...r} className={r.className}>
          <p className="text-[11px] uppercase tracking-[0.18em] text-[#4E6A52] font-medium">{H.eyebrow || 'Guests'}</p>
          <blockquote className="mt-6 font-serif text-2xl sm:text-3xl leading-snug text-[#2F3431] italic"
              style={{ fontFamily: '"Fraunces", "Cormorant Garamond", Georgia, serif' }}>
            &ldquo;{t.quote}&rdquo;
          </blockquote>
          <p className="mt-6 text-sm text-[#2F3431]/70">
            <span className="font-medium text-[#2F3431]">{t.name}</span> · {t.role}
          </p>
          {items.length > 1 && (
            <div className="mt-6 flex justify-center gap-1.5">
              {items.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setIdx(i)}
                  aria-label={`Testimonial ${i + 1}`}
                  className={`h-1.5 rounded-full transition-all ${idx === i ? 'w-6 bg-[#4E6A52]' : 'w-1.5 bg-[#D7E0D5]'}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

// ─── Final CTA ─────────────────────────────────────────────────
function FinalCTA({ onBookNow, cfg }) {
  const C = cfg?.final_cta || {};
  const r = useReveal(60);
  return (
    <section className="py-20 sm:py-28 bg-[#F7F6F2]" id="contact">
      <div className="max-w-[1240px] mx-auto px-5 sm:px-8">
        <div {...r} className={`relative rounded-[32px] overflow-hidden bg-[#4E6A52] p-10 sm:p-16 lg:p-20 text-center ${r.className}`}>
          <div className="pointer-events-none absolute -top-16 -left-16 h-64 w-64 rounded-full bg-white/8 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 -right-16 h-72 w-72 rounded-full bg-white/6 blur-3xl" />
          <h2 className="relative font-serif text-3xl sm:text-5xl text-white leading-tight max-w-2xl mx-auto"
              style={{ fontFamily: '"Fraunces", "Cormorant Garamond", Georgia, serif' }}>
            {C.title || 'Your next month, made effortless.'}
          </h2>
          <p className="relative mt-5 text-white/75 max-w-lg mx-auto text-[15px] sm:text-base">
            {C.subtitle || ''}
          </p>
          <button
            onClick={onBookNow}
            className="relative mt-9 inline-flex items-center gap-2 px-7 py-4 rounded-full bg-white text-[#4E6A52] text-[15px] font-medium hover:shadow-[0_18px_44px_-12px_rgba(0,0,0,0.3)] hover:-translate-y-0.5 transition-all"
            data-testid="final-book-now"
          >
            {C.button_label || 'Book Now'} <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </section>
  );
}

// ─── Footer ───────────────────────────────────────────────────
function Footer({ cfg }) {
  const F = cfg?.footer || {};
  const brand = cfg?.nav?.brand_name || 'Flex Living';
  const year = new Date().getFullYear();
  return (
    <footer className="bg-[#EEF2EC] border-t border-[#D7E0D5] py-14">
      <div className="max-w-[1240px] mx-auto px-5 sm:px-8 grid grid-cols-1 md:grid-cols-3 gap-10">
        <div>
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-full bg-[#4E6A52] flex items-center justify-center text-white">
              <Home className="h-4 w-4" strokeWidth={2.2} />
            </div>
            <span className="text-[15px] font-medium text-[#2F3431]">{brand}</span>
          </div>
          <p className="mt-4 text-sm text-[#2F3431]/60 max-w-xs leading-relaxed">
            {F.tagline || ''}
          </p>
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-[0.16em] text-[#4E6A52] font-medium">Explore</p>
          <ul className="mt-4 space-y-2 text-sm text-[#2F3431]/70">
            <li><a href="#homes" className="hover:text-[#4E6A52]">Homes</a></li>
            <li><a href="#whoitsfor" className="hover:text-[#4E6A52]">Who It&rsquo;s For</a></li>
            <li><a href="#howitworks" className="hover:text-[#4E6A52]">How It Works</a></li>
          </ul>
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-[0.16em] text-[#4E6A52] font-medium">Get in touch</p>
          <ul className="mt-4 space-y-2 text-sm text-[#2F3431]/70">
            {F.contact_email && <li><a href={`mailto:${F.contact_email}`} className="hover:text-[#4E6A52]">{F.contact_email}</a></li>}
            {F.location && <li>{F.location}</li>}
          </ul>
        </div>
      </div>
      <div className="max-w-[1240px] mx-auto px-5 sm:px-8 mt-10 pt-6 border-t border-[#D7E0D5] flex items-center justify-between gap-3 text-[11px] text-[#2F3431]/50">
        <p>© {year} {brand}. {F.copyright_suffix || ''}</p>
      </div>
    </footer>
  );
}

// ─── Page ─────────────────────────────────────────────────────
export default function FlexLivingLanding() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const bookNowHref = pathname === '/landing' ? '/listings' : '/booknow';
  const onBookNow = () => navigate(bookNowHref);

  const [cfg, setCfg] = useState(null);
  const [featuredHomes, setFeaturedHomes] = useState([]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [adminPass, setAdminPass] = useState(() =>
    (typeof window !== 'undefined' ? sessionStorage.getItem('app_password') || '' : '')
  );
  const [showLogin, setShowLogin] = useState(false);
  const [loginPw, setLoginPw] = useState('');
  const [loginBusy, setLoginBusy] = useState(false);
  const [loginErr, setLoginErr] = useState('');
  const isAdmin = !!adminPass;

  const tryAdminLogin = async () => {
    setLoginBusy(true); setLoginErr('');
    try {
      const res = await fetch(`${API}/api/auth/verify-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: loginPw }),
      });
      const data = await res.json();
      if (!data.valid) { setLoginErr('Incorrect password'); return; }
      sessionStorage.setItem('app_password', loginPw);
      setAdminPass(loginPw);
      setShowLogin(false); setLoginPw('');
    } catch {
      setLoginErr('Something went wrong');
    } finally {
      setLoginBusy(false);
    }
  };

  // Auto-open admin login when ?admin=1 is in the URL (bookmarkable for admins)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (new URLSearchParams(window.location.search).get('admin') === '1' && !adminPass) {
      setShowLogin(true);
    }
  }, [adminPass]);

  const loadAll = () => {
    fetch(`${API}/api/landing/config`).then(r => r.json()).then(setCfg).catch(() => {});
    fetch(`${API}/api/landing/featured-homes`).then(r => r.json())
      .then(d => setFeaturedHomes(d.homes || [])).catch(() => setFeaturedHomes([]));
  };
  useEffect(() => { loadAll(); }, []);

  // Preload the Fraunces serif once — used across every h1/h2/blockquote.
  useEffect(() => {
    if (document.getElementById('fraunces-font')) return;
    const link = document.createElement('link');
    link.id = 'fraunces-font';
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600&family=Inter:wght@400;500;600&display=swap';
    document.head.appendChild(link);
  }, []);

  const brand = cfg?.nav?.brand_name || 'Flex Living';
  const bookNowLabel = cfg?.hero?.book_now_label || 'Book Now';

  return (
    <div className="min-h-screen bg-[#F7F6F2] text-[#2F3431] scroll-smooth" style={{ fontFamily: '"Inter", system-ui, -apple-system, sans-serif' }}>
      <StickyNav onBookNow={onBookNow} brandName={brand} bookNowLabel={bookNowLabel} />
      <Hero onBookNow={onBookNow} cfg={cfg} />
      <ValueProps cfg={cfg} />
      <FeaturedHomes onBookNow={onBookNow} cfg={cfg} homes={featuredHomes} />
      <WhoItsFor cfg={cfg} />
      <HowItWorks cfg={cfg} />
      <Testimonials cfg={cfg} />
      <FinalCTA onBookNow={onBookNow} cfg={cfg} />
      <Footer cfg={cfg} />

      {/* Admin-only floating Edit button. Requires the site-wide app password
          (stored in sessionStorage after admin login). Hidden for public visitors. */}
      {isAdmin && (
        <button
          onClick={() => setEditorOpen(true)}
          className="fixed bottom-6 right-6 z-[60] group inline-flex items-center gap-2 px-4 py-3 rounded-full bg-[#4E6A52] text-white text-sm font-medium shadow-[0_16px_40px_-12px_rgba(78,106,82,0.55)] hover:bg-[#3d5541] hover:-translate-y-0.5 transition-all"
          data-testid="landing-edit-button"
        >
          <Pencil className="h-4 w-4" />
          <span>Edit page</span>
        </button>
      )}

      {/* Discreet Admin link at the very bottom-right for logging in from a
          public device (also triggerable via ?admin=1). Hidden once logged in. */}
      {!isAdmin && (
        <button
          onClick={() => setShowLogin(true)}
          className="fixed bottom-3 right-3 z-[50] text-[10px] text-[#2F3431]/30 hover:text-[#4E6A52] transition-colors px-2 py-1"
          data-testid="landing-admin-link"
        >
          Admin
        </button>
      )}

      {showLogin && (
        <div
          className="fixed inset-0 z-[80] bg-black/40 backdrop-blur-sm flex items-center justify-center px-5"
          onClick={() => setShowLogin(false)}
          data-testid="landing-admin-login"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-medium text-lg text-[#2F3431]">Admin sign-in</h3>
            <p className="mt-1 text-xs text-[#2F3431]/60">Enter the site password to edit this page.</p>
            <form
              onSubmit={(e) => { e.preventDefault(); tryAdminLogin(); }}
              className="mt-4 space-y-3"
            >
              <input
                type="password"
                autoFocus
                value={loginPw}
                onChange={(e) => setLoginPw(e.target.value)}
                placeholder="Site password"
                className="w-full px-3 py-2.5 rounded-lg border border-[#D7E0D5] focus:outline-none focus:border-[#4E6A52] focus:ring-2 focus:ring-[#4E6A52]/15 text-sm"
                data-testid="landing-admin-pw-input"
              />
              {loginErr && <p className="text-xs text-red-600">{loginErr}</p>}
              <div className="flex gap-2 justify-end pt-1">
                <button
                  type="button"
                  onClick={() => setShowLogin(false)}
                  className="px-4 py-2 text-sm text-[#2F3431]/70 hover:text-[#2F3431] rounded-full"
                >Cancel</button>
                <button
                  type="submit"
                  disabled={loginBusy || !loginPw}
                  className="px-4 py-2 text-sm bg-[#4E6A52] text-white rounded-full hover:bg-[#3d5541] disabled:opacity-60"
                  data-testid="landing-admin-submit"
                >
                  {loginBusy ? 'Signing in…' : 'Sign in'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isAdmin && editorOpen && cfg && (
        <LandingEditor
          open={editorOpen}
          onClose={() => setEditorOpen(false)}
          cfg={cfg}
          adminPass={adminPass}
          onSaved={(next) => { setCfg(next); loadAll(); }}
        />
      )}
    </div>
  );
}
